import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Lodash
import _noop from 'lodash/noop';
import _isNaN from 'lodash/isNaN';
import _toString from 'lodash/toString';
import _replace from 'lodash/replace';
import _toNumber from 'lodash/toNumber';
import _isNil from 'lodash/isNil';
import _isNull from 'lodash/isNull';
import _size from 'lodash/size';
import _max from 'lodash/max';

// utils
import isStringEmpty from '@tekion/tekion-base/utils/isStringEmpty';

// Components
import { getCurrencySymbol } from '@tekion/tekion-base/formatters/formatCurrency';
import NumberInput from '../NumberInput';

// Styles
import styles from './decimalInput.module.scss';

// helpers
import { getFormattedCurrencyValue, getFormattedNumberWithCommas } from './helpers/decimalInput.formatter';
import { getParsedValue, getPrecision } from './helpers/decimalInput.parser';

// context
import AmountInputFormatContext from '../../context/amountInputFormat.context';

// constants
import { DEFAULT_LOCALE, CURRENCY_VALUE_PRECISION } from './decimalInput.constants';
var DecimalInput = /*#__PURE__*/function (_PureComponent) {
  _inherits(DecimalInput, _PureComponent);
  var _super = _createSuper(DecimalInput);
  function DecimalInput(props) {
    var _this;
    _classCallCheck(this, DecimalInput);
    _this = _super.call(this, props);
    _defineProperty(_assertThisInitialized(_this), "handleInputFocus", function () {
      var onFocus = _this.props.onFocus;
      _this.setState({
        isFocused: true
      });
      onFocus();
    });
    _defineProperty(_assertThisInitialized(_this), "handleInputBlur", function () {
      var _assertThisInitialize, _assertThisInitialize2, _assertThisInitialize3, _assertThisInitialize4;
      var onBlur = _this.props.onBlur;
      _this.setState({
        isFocused: false,
        prevPrecision: CURRENCY_VALUE_PRECISION
      });
      onBlur((_assertThisInitialize = _assertThisInitialized(_this)) === null || _assertThisInitialize === void 0 ? void 0 : (_assertThisInitialize2 = _assertThisInitialize.ref) === null || _assertThisInitialize2 === void 0 ? void 0 : (_assertThisInitialize3 = _assertThisInitialize2.current) === null || _assertThisInitialize3 === void 0 ? void 0 : (_assertThisInitialize4 = _assertThisInitialize3.inputNumberRef) === null || _assertThisInitialize4 === void 0 ? void 0 : _assertThisInitialize4.rawInput);
    });
    _defineProperty(_assertThisInitialized(_this), "getValueWithMinus", function (value) {
      var stringValue = "".concat(value);
      var isValueWithMinus = stringValue.includes('-');
      var valueInNumber = _toNumber(stringValue);
      var _this$props = _this.props,
        isNegativeValue = _this$props.isNegativeValue,
        prevValue = _this$props.value;
      if (_isNaN(valueInNumber) && isValueWithMinus || isNegativeValue) {
        var hasEvenNumberOfMinuses = _size(stringValue.match(/-/g)) % 2 === 0;
        var valueWithoutMinus = stringValue.replace(/-/g, '');
        if (isNegativeValue && isStringEmpty(prevValue)) {
          var _valueWithMinus = "-".concat(valueWithoutMinus);
          return _valueWithMinus;
        }
        if (hasEvenNumberOfMinuses) {
          return valueWithoutMinus;
        }
        var valueWithMinus = "-".concat(valueWithoutMinus);
        return valueWithMinus;
      }
      return value;
    });
    _defineProperty(_assertThisInitialized(_this), "currencyValueFormatter", function (value) {
      var locale = _this.props.locale;
      if (isStringEmpty(value)) {
        return '';
      }
      if (value === '-' || value === '.') {
        return value;
      }
      var includeTwoDecimalPlacesAutomatically = _this.context;
      if (!includeTwoDecimalPlacesAutomatically) {
        return getFormattedNumberWithCommas(value);
      }
      var formattedCurrencyValue = getFormattedCurrencyValue(value, locale);
      return formattedCurrencyValue;
    });
    _defineProperty(_assertThisInitialized(_this), "handleChange", function (value) {
      var onChange = _this.props.onChange;
      if (!_isNull(value)) {
        // InputNumber triggers onChange with null value on blur
        onChange(value);
      }
    });
    _defineProperty(_assertThisInitialized(_this), "shiftCursorPosition", function () {
      var _this$ref, _this$ref$current, _this$ref$current$inp2;
      var shift = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      var _this$ref$current$inp = (_this$ref = _this.ref) === null || _this$ref === void 0 ? void 0 : (_this$ref$current = _this$ref.current) === null || _this$ref$current === void 0 ? void 0 : (_this$ref$current$inp2 = _this$ref$current.inputNumberRef) === null || _this$ref$current$inp2 === void 0 ? void 0 : _this$ref$current$inp2.input,
        selectionStart = _this$ref$current$inp.selectionStart;
      var newCursorPosition = _max([selectionStart + shift, 0]);
      window.requestAnimationFrame(function () {
        _this.ref.current.inputNumberRef.input.setSelectionRange(newCursorPosition, newCursorPosition);
      });
    });
    _defineProperty(_assertThisInitialized(_this), "currencyValueParser", function (value) {
      var _this$props2 = _this.props,
        precision = _this$props2.precision,
        prevValue = _this$props2.value,
        isNegativeValue = _this$props2.isNegativeValue;
      if (_isNil(value)) {
        return undefined;
      }
      if (value === '-' || value === '.') {
        return value;
      }
      if (value === '') {
        return undefined;
      }
      var valueWithMinus = _this.getValueWithMinus(value);
      var includeTwoDecimalPlacesAutomatically = _this.context;
      if (!includeTwoDecimalPlacesAutomatically) {
        var prevPrecision = _this.state.prevPrecision;
        var parsedValue = getParsedValue({
          valueWithMinus: valueWithMinus,
          prevPrecision: prevPrecision,
          shiftCursorPosition: _this.shiftCursorPosition,
          prevValue: prevValue,
          valueFromInput: value,
          isNegativeValue: isNegativeValue
        });
        var updatedPrecision = getPrecision(parsedValue);
        _this.setState({
          prevPrecision: updatedPrecision
        });
        return parsedValue;
      }
      var modifiedValue = _toNumber(_replace(_toString(valueWithMinus), /[.,]/g, ''));
      if (_isNaN(modifiedValue)) {
        return prevValue;
      }
      if (modifiedValue === 0) {
        if (prevValue === 0) {
          return undefined;
        }
      }
      // eslint-disable-next-line no-restricted-properties
      var divisor = Math.pow(10, precision);
      var decimalValue = modifiedValue / divisor;
      return decimalValue.toFixed(precision);
    });
    _this.state = {
      isFocused: false,
      prevPrecision: CURRENCY_VALUE_PRECISION
    };
    _this.ref = /*#__PURE__*/React.createRef();
    return _this;
  }
  _createClass(DecimalInput, [{
    key: "getIsInputMinus",
    value: function getIsInputMinus() {
      var _this$ref2, _this$ref2$current, _this$ref2$current$in;
      var shouldAllowNegativeSignInAmount = this.props.shouldAllowNegativeSignInAmount;
      return shouldAllowNegativeSignInAmount && (this === null || this === void 0 ? void 0 : (_this$ref2 = this.ref) === null || _this$ref2 === void 0 ? void 0 : (_this$ref2$current = _this$ref2.current) === null || _this$ref2$current === void 0 ? void 0 : (_this$ref2$current$in = _this$ref2$current.inputNumberRef) === null || _this$ref2$current$in === void 0 ? void 0 : _this$ref2$current$in.rawInput) === '-';
    }
  }, {
    key: "renderCurrency",
    value: function renderCurrency() {
      var currencySymbol = getCurrencySymbol();
      return /*#__PURE__*/React.createElement("div", {
        className: styles.currencyContainer
      }, /*#__PURE__*/React.createElement("div", {
        className: "".concat(styles.currency, " v-center")
      }, currencySymbol));
    }
  }, {
    key: "renderInput",
    value: function renderInput() {
      var _this$props3 = this.props,
        value = _this$props3.value,
        placeholder = _this$props3.placeholder,
        min = _this$props3.min,
        disabled = _this$props3.disabled,
        max = _this$props3.max,
        dataTest = _this$props3.dataTest;
      var valueToShow = this.getIsInputMinus() ? '-' : value;
      return /*#__PURE__*/React.createElement(NumberInput, {
        onChange: this.handleChange,
        value: valueToShow,
        className: styles.numberInput,
        onFocus: this.handleInputFocus,
        onBlur: this.handleInputBlur,
        placeholder: placeholder,
        shouldDisabledStepper: true,
        shouldDisableKeyHandlerForStepper: true,
        precision: CURRENCY_VALUE_PRECISION,
        min: min,
        max: max,
        disabled: disabled,
        parser: this.currencyValueParser,
        isRightAlign: true,
        formatter: this.currencyValueFormatter,
        innerRef: this.ref,
        dataTest: dataTest
      });
    }
  }, {
    key: "render",
    value: function render() {
      var _classNames;
      var isFocused = this.state.isFocused;
      var _this$props4 = this.props,
        hasError = _this$props4.hasError,
        className = _this$props4.className;
      return /*#__PURE__*/React.createElement("div", {
        className: classNames('flex', styles.container, className, (_classNames = {}, _defineProperty(_classNames, styles.currencyContainer__focused, isFocused), _defineProperty(_classNames, styles.currencyContainer__error, hasError), _classNames))
      }, this.renderCurrency(), this.renderInput());
    }
  }]);
  return DecimalInput;
}(PureComponent);
DecimalInput.contextType = AmountInputFormatContext;
DecimalInput.propTypes = {
  precision: PropTypes.number,
  onChange: PropTypes.func,
  value: PropTypes.number,
  // eslint-disable-line react/require-default-props
  hasError: PropTypes.bool,
  placeholder: PropTypes.bool,
  min: PropTypes.number,
  max: PropTypes.number,
  disabled: PropTypes.bool,
  onBlur: PropTypes.bool,
  onFocus: PropTypes.func,
  locale: PropTypes.string,
  className: PropTypes.string,
  dataTest: PropTypes.string,
  shouldAllowNegativeSignInAmount: PropTypes.bool,
  isNegativeValue: PropTypes.bool
};
DecimalInput.defaultProps = {
  precision: 2,
  onChange: _noop,
  hasError: false,
  placeholder: '0.00',
  min: undefined,
  max: undefined,
  disabled: false,
  onBlur: _noop,
  onFocus: _noop,
  locale: DEFAULT_LOCALE,
  className: '',
  dataTest: undefined,
  shouldAllowNegativeSignInAmount: false,
  isNegativeValue: false
};
export default DecimalInput;